import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import { Gift, Heart, Volume2, Loader2, Sparkles, ArrowRight } from "lucide-react";
import { api, absUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";

const hwClass = (id) => ({ classic: "hw-classic", script: "hw-script", casual: "hw-casual" }[id] || "hw-script");

function fireConfetti() {
  const colors = ["#7DA083", "#2E4A3B", "#F5F2E9", "#D4AF37", "#CDBFA8"];
  confetti({ particleCount: 90, spread: 72, origin: { y: 0.65 }, colors });
  setTimeout(() => confetti({ particleCount: 50, angle: 60, spread: 55, origin: { x: 0 }, colors }), 180);
  setTimeout(() => confetti({ particleCount: 50, angle: 120, spread: 55, origin: { x: 1 }, colors }), 180);
}

export default function GiftPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [opened, setOpened] = useState(false);

  // Keep this page private-by-link: ask crawlers not to index it.
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  useEffect(() => {
    let on = true;
    setLoading(true);
    api.get(`/personalizations/${token}`)
      .then((r) => { if (on) { setData(r.data); setError(false); } })
      .catch(() => { if (on) setError(true); })
      .finally(() => { if (on) setLoading(false); });
    return () => { on = false; };
  }, [token]);

  const open = () => {
    setOpened(true);
    fireConfetti();
  };

  if (loading) {
    return (
      <div className="grid min-h-[70vh] place-items-center wp-hero-gradient">
        <Loader2 className="h-8 w-8 animate-spin text-wp-rose-strong" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="grid min-h-[70vh] place-items-center wp-hero-gradient px-4 text-center" data-testid="gift-not-found">
        <div className="max-w-md">
          <span className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-white text-wp-rose-strong shadow-wp-soft"><Gift className="h-8 w-8" /></span>
          <h1 className="mt-5 font-display text-3xl">This gift couldn't be found</h1>
          <p className="mt-2 text-wp-ink-muted">The link may be incorrect or the gift page is no longer available.</p>
          <Button asChild className="mt-6 rounded-xl bg-wp-rose-strong text-white"><Link to="/">Visit Wonder Parcel <ArrowRight className="ml-1.5 h-4 w-4" /></Link></Button>
        </div>
      </div>
    );
  }

  const photo = absUrl(data.photo_url);
  const voice = absUrl(data.voice_url);

  return (
    <div className="relative min-h-[80vh] wp-hero-gradient" data-testid="gift-page">
      <div className="wp-noise pointer-events-none absolute inset-0" />
      <div className="relative mx-auto w-full max-w-xl px-4 py-12 sm:py-16">
        <AnimatePresence mode="wait">
          {!opened ? (
            /* ---------- Unopened: gift reveal ---------- */
            <motion.div key="closed" exit={{ opacity: 0, scale: 0.96 }} className="text-center" data-testid="gift-unopened">
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                <p className="wp-label text-xs text-wp-rose-strong">A gift for</p>
                <h1 className="mt-2 font-display text-4xl sm:text-5xl">{data.recipient_name || "you"}</h1>
                <p className="mt-3 text-wp-ink-muted">Someone put their heart into this. Tap to open it.</p>
              </motion.div>
              <motion.button
                onClick={open}
                data-testid="gift-open-button"
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.15, type: "spring", stiffness: 180, damping: 14 }}
                whileHover={{ y: -4 }} whileTap={{ scale: 0.97 }}
                className="group mx-auto mt-10 grid h-44 w-44 place-items-center rounded-wp-xl bg-wp-rose-strong text-white shadow-wp-card"
              >
                <motion.span animate={{ rotate: [0, -3, 3, -3, 0] }} transition={{ repeat: Infinity, duration: 2.4, ease: "easeInOut" }}>
                  <Gift className="h-20 w-20" />
                </motion.span>
              </motion.button>
              <p className="mt-6 flex items-center justify-center gap-1.5 text-xs text-wp-ink-muted"><Sparkles className="h-3.5 w-3.5 text-wp-ocean" /> Open your gift</p>
            </motion.div>
          ) : (
            /* ---------- Opened: full content ---------- */
            <motion.div key="open" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, ease: [0.2, 0.8, 0.2, 1] }}
              className="overflow-hidden rounded-wp-xl border border-border bg-white/95 shadow-wp-card backdrop-blur" data-testid="gift-opened">
              {/* header */}
              <div className="bg-wp-blush-50 px-6 pt-7 pb-5 text-center">
                <span className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-white text-wp-rose-strong shadow-wp-soft"><Heart className="h-6 w-6" /></span>
                <p className="wp-label mt-3 text-[10px] text-wp-rose-strong">{data.occasion ? `Happy ${data.occasion}` : "For you, with love"}</p>
                <h1 className="mt-1 font-display text-3xl sm:text-4xl" data-testid="gift-recipient-name">Dear {data.recipient_name || "You"}</h1>
              </div>

              {/* photo */}
              {photo && (
                <div className="px-6 pt-6">
                  <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2 }}
                    className="overflow-hidden rounded-wp-lg border border-border shadow-wp-soft">
                    <img src={photo} alt={`A memory for ${data.recipient_name}`} className="aspect-[4/3] w-full object-cover" data-testid="gift-photo" />
                  </motion.div>
                </div>
              )}

              {/* letter */}
              {data.ai_letter && (
                <div className="px-6 pt-6" data-testid="gift-letter">
                  <p className="whitespace-pre-line text-[15px] text-wp-ink/90" style={{ fontFamily: "'Playfair Display', Georgia, serif", lineHeight: 1.8 }}>
                    {data.ai_letter}
                  </p>
                </div>
              )}

              {/* short handwritten note */}
              {data.gift_note && (
                <div className="px-6 pt-5">
                  <p className={`text-3xl leading-snug text-wp-rose-strong ${hwClass(data.handwriting_style)}`} data-testid="gift-note">{data.gift_note}</p>
                </div>
              )}

              {/* voice */}
              {voice && (
                <div className="px-6 pt-6" data-testid="gift-voice">
                  <div className="rounded-wp-lg border border-wp-mint/50 bg-wp-mint/10 p-4">
                    <p className="mb-2 flex items-center gap-2 text-sm font-medium text-wp-ocean"><Volume2 className="h-4 w-4" /> Listen to a voice message</p>
                    <audio src={voice} controls className="w-full" data-testid="gift-voice-audio" />
                  </div>
                </div>
              )}

              {/* footer / branding */}
              <div className="mt-7 border-t border-border bg-wp-cream-100/60 px-6 py-5 text-center">
                <p className="flex items-center justify-center gap-1.5 text-xs text-wp-ink-muted"><Gift className="h-4 w-4 text-wp-rose-strong" /> Hand-packed with love by <span className="font-semibold text-wp-ink">Wonder Parcel</span></p>
                <Link to="/" data-testid="gift-visit-store-link" className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-wp-rose-strong hover:underline">
                  Create your own gift <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
